import { fleetEventSchema, type FleetEvent } from '@kraaapf/contracts';

import type { PortalClient } from './http-client';

/**
 * Der Live-Strom der Ereignisse.
 *
 * ## Warum `EventSource` und kein `fetch`
 *
 * Weil der Browser die Wiederverbindung selbst übernimmt — mitsamt
 * Rückstaffelung und `Last-Event-ID`. Das ist genau der Teil, den man von Hand
 * falsch baut: Ein Dashboard, das nach einem kurzen Netzaussetzer stumm
 * bleibt, sieht aus wie ein Dashboard mit einer ruhigen Flotte.
 *
 * Der Preis ist, dass `EventSource` **keine Header setzen kann**. Deshalb
 * nimmt `GET /events` seit M7 das Sitzungs-Cookie an; ohne diese Änderung gäbe
 * es hier keine Live-Ansicht.
 *
 * ## Was der Strom nicht ist
 *
 * Keine Quelle der Wahrheit. Redis Pub/Sub stellt „höchstens einmal" zu, ohne
 * Persistenz; ein verlorenes Ereignis bedeutet eine kurz veraltete Anzeige.
 * Die Oberfläche behandelt ein Ereignis deshalb als **Anlass**, ihre Daten neu
 * zu holen — und nicht als Datensatz, den sie in ihren Zustand einträgt.
 */
export interface EventStreamHandlers {
  readonly onEvent: (event: FleetEvent) => void;
  /** Verbindung steht. Wird nach jeder Wiederverbindung erneut gerufen. */
  readonly onOpen?: () => void;
  /**
   * Verbindung verloren.
   *
   * Kein Grund zur Panik und kein Grund, etwas abzuräumen: Der Browser
   * versucht es von selbst erneut. Die Oberfläche zeigt lediglich an, dass die
   * Anzeige gerade nicht live ist.
   */
  readonly onError?: () => void;
}

export interface EventStream {
  close: () => void;
}

export function openEventStream(
  client: PortalClient,
  handlers: EventStreamHandlers,
  options: { nodeId?: string } = {},
): EventStream {
  const url = new URL(client.urlFor('/events'), globalThis.location.origin);

  if (options.nodeId !== undefined) {
    url.searchParams.set('nodeId', options.nodeId);
  }

  // `withCredentials`: Ohne diese Angabe schickt der Browser das
  // Sitzungs-Cookie nicht mit, sobald Oberfläche und API nicht dieselbe
  // Herkunft haben — also genau im Entwicklungsserver.
  const source = new EventSource(url.toString(), { withCredentials: true });

  source.addEventListener('open', () => handlers.onOpen?.());
  source.addEventListener('error', () => handlers.onError?.());

  // Der Master benennt jedes Ereignis nach seinem Typ (`node.heartbeat`,
  // `site.state-changed`). Ein `message`-Handler bekäme davon nichts zu sehen:
  // Benannte Ereignisse landen nicht bei `onmessage`.
  for (const type of EVENT_TYPES) {
    source.addEventListener(type, (message: MessageEvent<string>) => {
      const parsed = parseEvent(message.data);

      if (parsed !== null) {
        handlers.onEvent(parsed);
      }
    });
  }

  return {
    close: () => {
      source.close();
    },
  };
}

/**
 * Prüft ein eingehendes Ereignis gegen den Vertrag.
 *
 * Ein Ereignis, das nicht passt, wird **verworfen** und nicht angezeigt — die
 * gleiche Haltung wie im Ereignisbus des Masters. Auf dem Kanal kann eine
 * Master-Instanz mit abweichender Version schreiben, und ein unvalidiertes
 * Ereignis liefe als scheinbar gültiges Objekt bis in die Oberfläche.
 */
function parseEvent(raw: string): FleetEvent | null {
  try {
    const parsed = fleetEventSchema.safeParse(JSON.parse(raw));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Die Namen, auf die gehört wird.
 *
 * Aus dem Vertrag abgeleitet und nicht abgetippt: Ein neuer Ereignistyp ist
 * damit automatisch dabei. Der `keepalive`-Takt kommt dazu — er trägt keine
 * Nutzlast, hält aber die Verbindung offen, und ohne ihn schlösse ein Proxy
 * sie nach seiner Leerlauffrist.
 */
const EVENT_TYPES: readonly string[] = fleetEventSchema.options.map(
  (option) => option.shape.type.value,
);
